import { createAsyncThunk, createSelector, createSlice, type PayloadAction } from "@reduxjs/toolkit"
import { productLevelFilter, roleFilter, sortRoles } from "@utils/utils"
import _ from "lodash"
import axios, { type AxiosError } from "axios"
import type { RootState } from "../store"

interface LoginPayload {
	profile: unknown
	expires: unknown
	language: string
	age: unknown
	palloid: unknown
	sporttiId: unknown
	settings: unknown
	roles: unknown
	firstname: string
	lastname: string
	[key: string]: unknown
}

interface UserState {
	isSuccess: boolean
	isError: boolean
	isFetching: boolean
	username: string
	// Domain objects with loosely-typed shapes consumed across the app; kept permissive on purpose
	profileDataForCtx: any
	errorMessage: string
	groups: any[]
	rootGroups: any[]
	selectedGroup: any
	contextData: any
	// Currently chosen child of the user; used as the 'x-child-id' header value
	childId: string
	roles?: string[]
	selectedRole?: any
	productLevel?: any
	profile?: any
	pendingChildAccounts?: any
	[key: string]: any
}

// Gets resulting state object from login-type API response
const getLoginStateObj = (payload: LoginPayload) => {
	const payloadRoles = (Array.isArray(payload.roles) ? payload.roles : []) as string[]
	const roles = roleFilter(payloadRoles)
	const productLevel = productLevelFilter(payloadRoles)
	const obj = {
		profile: payload.profile,
		expires: payload.expires,
		language: payload.language,
		age: payload.age,
		palloid: payload.palloid,
		sporttiId: payload.sporttiId,
		settings: payload.settings,
		roles,
		selectedRole: sortRoles(roles)[0],
		productLevel: sortRoles(productLevel)[0],
		username: `${payload.firstname} ${payload.lastname}`,
		lastname: payload.firstname,
		firstname: payload.lastname,
	}
	return obj
}
export const changePassword = createAsyncThunk("user/reset-password", async (email: unknown, thunkAPI) => {
	try {
		const response = await axios.post("/user/forgotten", email)
		if (response.status === 200) {
			return response.data
		} else {
			return thunkAPI.rejectWithValue(response.data)
		}
	} catch (error) {
		return thunkAPI.rejectWithValue((error as AxiosError).response?.data)
	}
})

const initialState: UserState = {
	isSuccess: false,
	isError: false,
	isFetching: false,
	username: "",
	profileDataForCtx: {
		children: [],
	},
	errorMessage: "",
	groups: [],
	rootGroups: [],
	selectedGroup: null,
	contextData: {
		rootGroup: "",
		group: "",
		groupRole: "",
	},
	// Currently chosen child of the user; used as the 'x-child-id' header value
	childId: "",
}

export const userSlice = createSlice({
	name: "user",
	initialState,
	reducers: {
		setLoginData(state, { payload }: PayloadAction<LoginPayload>) {
			const obj = getLoginStateObj(payload)
			_.assign(state, obj)
		},
		setUsername(state, { payload }: PayloadAction<string>) {
			state.username = payload
		},
		setSelectedRole(state, { payload }: PayloadAction<string>) {
			state.selectedRole = payload
		},
		setContextData(state, { payload }: PayloadAction<any>) {
			state.contextData = payload
		},
		resetState(state) {
			state.isSuccess = false
			state.isError = false
			state.isFetching = false
		},
		setProfileDataForCtx(state, { payload }: PayloadAction<Record<string, unknown>>) {
			// Select only paths used in permission checking to reduce bloat
			const obj = _.pick(payload, [
				"_id",
				"features",
				"groups",
				"rootGroups",
				"activeMemberRole",
				"patronRootGroups",
				"groupRoleOverview",
				"dnInfo",
				"parents",
				"children",
				"operationalClass",
				"processClasses",
				"levelClasses",
				"opClassList",
			]) as Record<string, unknown> & { children: unknown[] }
			const roleData = _.get(payload, "user.roles") as Array<{ name: string }> | undefined
			if (roleData) {
				state.roles = roleData.map((r) => r.name)
			}
			state.profileDataForCtx = obj
		},
		setPendingChildAccounts(state, { payload }: PayloadAction<unknown>) {
			state.pendingChildAccounts = payload
		},
		setChildId(state, { payload }: PayloadAction<string>) {
			state.childId = payload
		},
		setProductLevel(state, { payload }: PayloadAction<number | string>) {
			let val = payload
			if (_.isInteger(payload)) {
				// Cast to "prodX" string; currently checked for in code
				val = `prod${val}`
			}
			state.productLevel = val as string
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(changePassword.fulfilled, (state) => {
				state.isSuccess = true
				state.isFetching = false
				state.isError = false
			})
			.addCase(changePassword.rejected, (state, { payload }) => {
				state.isSuccess = false
				state.isFetching = false
				state.isError = true
				state.errorMessage = payload ? (payload as string) : "Unknown error!"
			})
	},
})

export const {
	setUsername,
	setSelectedRole,
	resetState,
	setContextData,
	setLoginData,
	setPendingChildAccounts,
	setProfileDataForCtx,
	setChildId,
	setProductLevel,
} = userSlice.actions

const selectUserSlice = (state: RootState) => state.user

export const userSelector = createSelector([selectUserSlice], (user) => {
	return {
		...user,

		// Update profile to be a derived value dependent on childId existence
		profile: user?.childId || user?.profile,

		// Provide original static profile as 'baseProfile', use when needed
		baseProfile: user?.profile,

		isAdmin: ["admin", "expert"].includes(user?.selectedRole),
		isAdminWithCtx: ["admin", "expert"].includes(user?.selectedRole) && user?.contextData?.rootGroup,
		isPlainAdmin: ["admin", "expert"].includes(user?.selectedRole) && !user?.contextData?.rootGroup,
		isPrivilegedAdmin: ["admin"].includes(user?.selectedRole) && !user?.contextData?.rootGroup,
		isPatron: !!user?.contextData?.rootGroup && user?.contextData?.groupRole === "patron",
		hasClubSupportAdmin: user?.roles?.includes?.("club_support_admin"),
	}
})
